// Spotify API Service
const SPOTIFY_API_BASE = 'https://api.spotify.com/v1';
const SPOTIFY_AUTH_URL = 'https://accounts.spotify.com/authorize';
const SPOTIFY_TOKEN_URL = 'https://accounts.spotify.com/api/token';
const PKCE_VERIFIER_KEY = 'spotify_pkce_verifier';

const base64UrlEncode = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateRandomString = (length = 64) => {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const createCodeChallenge = async (verifier) => {
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

const getAuthUrl = async () => {
  const verifier = generateRandomString();
  const challenge = await createCodeChallenge(verifier);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    client_id: process.env.REACT_APP_SPOTIFY_CLIENT_ID,
    response_type: 'code',
    redirect_uri: process.env.REACT_APP_SPOTIFY_REDIRECT_URI,
    scope:
      'streaming user-read-private user-read-email user-library-read playlist-read-private playlist-read-collaborative user-read-playback-state user-modify-playback-state',
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });
  return `${SPOTIFY_AUTH_URL}?${params.toString()}`;
};

const getAccessToken = async (code) => {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (!verifier) {
    throw new Error('Missing PKCE code verifier');
  }

  const response = await fetch(SPOTIFY_TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: process.env.REACT_APP_SPOTIFY_REDIRECT_URI,
      client_id: process.env.REACT_APP_SPOTIFY_CLIENT_ID,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to get access token');
  }

  const data = await response.json();
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  return data;
};

const loadSpotifySdk = () =>
  new Promise((resolve, reject) => {
    if (window.Spotify) {
      resolve();
      return;
    }

    const existing = document.querySelector('script[src="https://sdk.scdn.co/spotify-player.js"]');
    if (existing) {
      window.onSpotifyWebPlaybackSDKReady = () => resolve();
      existing.addEventListener('error', reject);
      return;
    }

    window.onSpotifyWebPlaybackSDKReady = () => resolve();

    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    script.onerror = reject;
    document.body.appendChild(script);
  });

const spotifyRequest = async (url, accessToken, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    let details = '';
    try {
      const errorBody = await response.json();
      details = errorBody?.error?.message || JSON.stringify(errorBody);
    } catch {
      // ignore parse errors
    }
    const message = `Spotify request failed (${response.status})${details ? `: ${details}` : ''}`;
    throw new Error(message);
  }

  return response;
};

const fetchWithAuth = async (url, accessToken) => {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    let details = '';
    try {
      const errorBody = await response.json();
      details = errorBody?.error?.message || JSON.stringify(errorBody);
    } catch {
      // ignore parse errors
    }
    const message = `Failed to fetch tracks (${response.status})${details ? `: ${details}` : ''}`;
    throw new Error(message);
  }

  return response.json();
};

const transferPlayback = async (accessToken, deviceId) =>
  spotifyRequest(`${SPOTIFY_API_BASE}/me/player`, accessToken, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_ids: [deviceId], play: false }),
  });

const startPlayback = async (accessToken, uri, deviceId = null) => {
  const target = deviceId
    ? `${SPOTIFY_API_BASE}/me/player/play?device_id=${deviceId}`
    : `${SPOTIFY_API_BASE}/me/player/play`;
  return spotifyRequest(target, accessToken, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uris: [uri] }),
  });
};

const pausePlayback = async (accessToken, deviceId = null) => {
  const target = deviceId
    ? `${SPOTIFY_API_BASE}/me/player/pause?device_id=${deviceId}`
    : `${SPOTIFY_API_BASE}/me/player/pause`;
  return spotifyRequest(target, accessToken, {
    method: 'PUT',
  });
};

const getDevices = async (accessToken) => {
  const response = await spotifyRequest(`${SPOTIFY_API_BASE}/me/player/devices`, accessToken);
  const data = await response.json();
  return data.devices || [];
};

const getMe = async (accessToken) => {
  const response = await spotifyRequest(`${SPOTIFY_API_BASE}/me`, accessToken);
  return response.json();
};

const getUserPlaylists = async (accessToken) => {
  const playlists = [];
  let nextUrl = `${SPOTIFY_API_BASE}/me/playlists?limit=50`;

  while (nextUrl) {
    const data = await fetchWithAuth(nextUrl, accessToken);
    playlists.push(...data.items);
    nextUrl = data.next;
  }

  return playlists.map((playlist) => ({
    id: playlist.id,
    name: playlist.name,
    owner: playlist.owner?.display_name || 'Unknown',
    total: playlist.tracks?.total ?? 0,
  }));
};

const pickRandomPlayableTrack = (tracks) => {
  const playableTracks = tracks.filter(
    (track) => track?.uri && !track.is_local && track.is_playable !== false
  );
  if (playableTracks.length === 0) {
    return null;
  }
  return playableTracks[Math.floor(Math.random() * playableTracks.length)];
};

const extractPlaylistId = (input) => {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  const uriMatch = trimmed.match(/^spotify:playlist:([A-Za-z0-9]+)$/);
  if (uriMatch) return uriMatch[1];

  const normalized =
    trimmed.startsWith('http://') || trimmed.startsWith('https://')
      ? trimmed
      : `https://${trimmed}`;

  try {
    const url = new URL(normalized);
    const pathMatch = url.pathname.match(/\/playlist\/([A-Za-z0-9]+)/);
    if (pathMatch) return pathMatch[1];
  } catch {
    // not a URL
  }

  const idMatch = trimmed.match(/^[A-Za-z0-9]+$/);
  return idMatch ? trimmed : null;
};

const getRandomTrack = async (accessToken, playlistId = null, seenIds = new Set()) => {
  try {
    let nextUrl = playlistId
      ? `${SPOTIFY_API_BASE}/playlists/${playlistId}/tracks?limit=50`
      : `${SPOTIFY_API_BASE}/me/tracks?limit=50`;

    while (nextUrl) {
      const data = await fetchWithAuth(nextUrl, accessToken);
      const tracks = data.items.map((item) => item.track).filter(Boolean);
      if (tracks.length === 0 && !data.next) {
        throw new Error(
          playlistId
            ? 'No tracks found in this playlist'
            : 'No saved tracks found in your Spotify library'
        );
      }

      const unseenTracks = tracks.filter((track) => !seenIds.has(track.id));
      const randomTrack = pickRandomPlayableTrack(unseenTracks);
      if (randomTrack) {
        const releaseDate = randomTrack.release_date || randomTrack.album?.release_date || null;
        const parsedYear = releaseDate ? Number.parseInt(releaseDate.slice(0, 4), 10) : NaN;
        const year = Number.isFinite(parsedYear) ? parsedYear : '0000';
        return {
          id: randomTrack.id,
          name: randomTrack.name,
          artist: randomTrack.artists[0].name,
          artists: randomTrack.artists.map((a) => a.name).join(', '),
          year,
          releaseDate,
          previewUrl: randomTrack.preview_url,
          images: randomTrack.album.images,
          uri: randomTrack.uri,
        };
      }

      nextUrl = data.next;
    }

    throw new Error('No playable tracks found that you have not played yet');
  } catch (error) {
    console.error('Error fetching random track:', error);
    throw error;
  }
};

export {
  getAuthUrl,
  getAccessToken,
  getRandomTrack,
  getUserPlaylists,
  extractPlaylistId,
  loadSpotifySdk,
  transferPlayback,
  startPlayback,
  pausePlayback,
  getDevices,
  getMe,
};
